import { useMemo, useSyncExternalStore } from "react";

/**
 * The app's one breakpoint ladder.
 *
 * These are antd's own `xs/sm/md/lg/xl/xxl` values, deliberately: the grid is
 * already driven by them in ~68 places and most of the stylesheets already key
 * off 575/576, 767/768, 992/993 and 1200. Inventing a second ladder would have
 * left the CSS and the grid disagreeing by a pixel or two at every edge, which
 * is where the one-off `399px`/`401px`/`430px` rules came from in the first
 * place. New rules should land on these numbers.
 *
 * Note the CSS side uses `max-width: 575px` where JS asks `width < 576` — the
 * same edge, expressed the way each language expects it.
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

/**
 * One resize subscription for the whole app.
 *
 * Every previous call site did `addEventListener("resize", ...)` inside an
 * effect with no cleanup, so listeners accumulated for the life of the page —
 * and because the effects depended on the width they had just set, each resize
 * registered another one. A single module-level listener, shared by every
 * consumer and removed once the last one unmounts, replaces all of that.
 *
 * It is throttled to one frame: a window drag fires `resize` continuously, and
 * the old code ran a React render per event.
 */
let width = typeof window === "undefined" ? BREAKPOINTS.lg : window.innerWidth;

const listeners = new Set();
let frame = 0;

const publish = () => {
  frame = 0;

  const next = window.innerWidth;

  // Vertical-only resizes (the mobile URL bar collapsing, a devtools split)
  // fire `resize` without changing width. Bailing keeps them free.
  if (next === width) return;

  width = next;
  listeners.forEach((notify) => notify());
};

const onResize = () => {
  if (frame) return;
  frame = window.requestAnimationFrame(publish);
};

const subscribe = (listener) => {
  if (!listeners.size) window.addEventListener("resize", onResize);

  listeners.add(listener);

  return () => {
    listeners.delete(listener);

    if (!listeners.size) {
      window.removeEventListener("resize", onResize);

      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
    }
  };
};

/**
 * `useSyncExternalStore` rather than `useState` + an effect: the snapshot is
 * read during render, so a component never paints one frame at a stale width,
 * and React handles the tearing that a shared mutable `width` would otherwise
 * cause across concurrent renders.
 */
const getSnapshot = () => width;

/**
 * The raw viewport width. This is the drop-in for the old `resizeWidth`, so
 * existing `resizeWidth > 575` comparisons keep working untouched.
 */
export const useWindowWidth = () => useSyncExternalStore(subscribe, getSnapshot);

/**
 * Named flags, for new code. Prefer these over bare pixel comparisons:
 * `isMobile` says what the branch means, `width < 576` makes the next reader
 * work out which edge that is.
 *
 * Each flag is "this size or smaller/larger", matching how the CSS reads.
 */
const useBreakpoint = () => {
  const viewport = useWindowWidth();

  return useMemo(
    () => ({
      width: viewport,
      isMobile: viewport < BREAKPOINTS.sm,
      isTablet: viewport >= BREAKPOINTS.sm && viewport < BREAKPOINTS.lg,
      isDesktop: viewport >= BREAKPOINTS.lg,
      isWide: viewport >= BREAKPOINTS.xxl,
      upTo: (key) => viewport < BREAKPOINTS[key],
      from: (key) => viewport >= BREAKPOINTS[key],
    }),
    [viewport]
  );
};

export default useBreakpoint;
